package net.sergeych.kilogin.client

import net.sergeych.bintools.encodeToHex
import net.sergeych.crypto2.KDF
import net.sergeych.crypto2.SealedBox
import net.sergeych.crypto2.SigningSecretKey
import net.sergeych.crypto2.SymmetricKey
import net.sergeych.bintools.ByteChunk
import net.sergeych.kilogin.*
import net.sergeych.kilogin.client.Registration.Result.*
import net.sergeych.kilogin.server.RestoreAccessPayload
import net.sergeych.kiloparsec.KiloClient
import net.sergeych.mp_logger.LogTag
import net.sergeych.mp_logger.debug
import net.sergeych.mp_logger.error
import net.sergeych.mp_logger.exception
import net.sergeych.utools.pack

/**
 * Registration instances are used to perform superlogin-compatible registration in a smart way
 * avoiding unnecessary time-consuming password derivation and key generation repetitions.
 * once calculated these values are kept in the instance unless new password or different
 * password derivation parameters are supplied. This is useful in the case the login in user error
 * result - user can provide new login and re-register times faster than otherwise re-deriving
 * and creating new keys.
 *
 * It is important to use _different_ instances for different registrations, and the same instance
 * for consecutive attempts to register the same user varying login only.
 *
 * See [register] for more.
 *
 * @param client KiloScope instance to be used for registration
 * @param passwordComplexity KDF.Complexity to be used for password derivation (recommended Moderate or higher)
 */
class Registration(
    private val client: KiloClient<*>,
    private val passwordComplexity: KDF.Complexity = KDF.Complexity.Moderate,
    private val acoComplexity: KDF.Complexity = KDF.Complexity.Sensitive,
    private val loginTransformer: (String) -> ByteChunk
) : LogTag("SLREG") {

    /**
     * The result of the Kilogin registration. Successful registration
     * provides a __secret__: a string that is the only hope to reset lost password,
     * data storage key and more. see [Success]. This is why result is a sealed class
     * not just enum.
     */
    @Suppress("unused")
    sealed class Result {
        /**
         * Login is already in use or is somehow else invalid
         */
        object InvalidLogin : Result()

        /**
         * Operation failed for unknown reason, usually it means
         * network or server downtime
         */
        class NetworkFailure(val exception: Throwable? = null) : Result()

        /**
         * Successful registration
         */
        class Success(
            /**
             * The secret code that can be used to reset password. Without it, it is impossible
             * to restore access tp keys, etc.
             */
            val secret: String,
            /**
             * A new random symmetric key, safely stored with login credentials and available
             * after successful sign-in, can be used to protect sensitive user data.
             */
            val dataKey: SymmetricKey,
            /**
             * This is application-specific data generated by the application
             * server while registering new client.
             */
            val encodedData: UByteArray?
        ) : Result() {
            inline fun <reified D> data() = encodedData?.let {
                biDecode<D>(it)
            }
        }

        /**
         * Other errors are reserved for application-specific errors,
         * like missing/wrong invitation code, quota exceeded, etc.
         */
        class OtherError(val code: String, val packedData: UByteArray? = null) : Result()
    }

    private var passwordKeys: DerivedKeys? = null
    private val loginKey by lazy { SigningSecretKey.new() }
    private val dataKey = SymmetricKey.new()

    suspend inline fun <reified T> register(
        login: String,
        password: String,
        extraData: T? = null,
    ): Result = registerWithData(login, password, extraData?.let { pack(it) })


    /**
     * Smart attempt to register. It is ok to repeatedly call it if the result is not [Result.Success]: it will
     * cache internal data and reuse time-consuming precalculated values for caches and derived keys if the
     * password and derivation parameters are not changed between calls.
     */
    suspend fun registerWithData(
        login: String,
        password: String,
        extraData: UByteArray? = null,
        secretData: UByteArray? = null,
    ): Result {
        passwordKeys = bm("derive password keys") { DerivedKeys.derive(password, passwordComplexity) }
        val nonce = client.token()
        val spl = RestoreAccessPayload(login, loginKey, dataKey, secretData)
        val (restoreKey, restoreData) = AccessControlObject.pack(passwordKeys!!.loginAccessKey, spl, acoComplexity)
        try {
            val ra = RegistrationArgs(
                nonce,
                loginTransformer(login),
                passwordKeys!!.loginId,
                loginKey.verifyingKey,
                passwordKeys!!.pbkdParams,
                restoreKey.restoreId, restoreData,
                extraData
            )

            val result = client.call(slRegister, SealedBox.encode<RegistrationArgs>(ra, loginKey))
            when (result) {
                AuthenticationResult.RestoreIdUnavailable -> {
                    // rare situation but still possible: just repack the ACO
                    debug { "retrying registration on restoreId clash" }
                }

                AuthenticationResult.LoginIdUnavailable -> {
                    // rare situation: loginId is already in use. We have to re-derive password keys
                    // using new random salt:
                    debug { "retrying registration on loginId clash" }
                    passwordKeys = DerivedKeys.derive(password, passwordComplexity)
                }

                AuthenticationResult.LoginUnavailable -> return Result.InvalidLogin

                is AuthenticationResult.OtherError -> return OtherError(result.reason, result.packedData)

                is AuthenticationResult.Success -> {
                    return Success(
                        restoreKey.secret,
                        dataKey,
                        result.extraData
                    )
                }

                AuthenticationResult.ConnectionIsLoggedIn -> return Result.OtherError("already logged in")
            }
        } catch (x: Throwable) {
            exception { "Failed to register" to x }
        }
        // If we still get there, it's a strange error - 10 times we cant get suitable restoreId...
        error { "Failed to register after 10 repetitions with no apparent reason" }
        return Result.NetworkFailure(null)
    }

}